import { api, newsInput, type NewsInput, queryKeys } from '@alpha/api-client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PageHeader } from '../../components/PageHeader';
import { formatDateTime } from '../../lib/format';
import { Button, Card, Chip, FormGroup, Input, Spinner, Textarea } from '../../ui';

const SYSTEM_TABS = [
  { label: 'News', to: '/system/news' },
  { label: 'Settings', to: '/system/settings' },
  { label: 'Cron Monitor', to: '/system/cron-log' },
];

export function News() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.news() });

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.news(),
    queryFn: () => api.admin.news().then((r) => r.data),
  });

  const form = useForm<NewsInput>({
    resolver: zodResolver(newsInput),
    defaultValues: { title: '', body: '', is_published: true },
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const create = useMutation({
    mutationFn: (values: NewsInput) => {
      if (!imageFile) return api.admin.addNews(values);

      // multipart/form-data — mirrors uploadSettingsQr's FormData convention.
      const formData = new FormData();
      formData.append('title', values.title);
      formData.append('body', values.body);
      formData.append('is_published', values.is_published ? '1' : '0');
      formData.append('image', imageFile);
      return api.admin.addNews(formData);
    },
    onSuccess: () => {
      form.reset();
      setImageFile(null);
      if (imageInputRef.current) imageInputRef.current.value = '';
      invalidate();
    },
  });

  const remove = useMutation({ mutationFn: (id: string) => api.admin.deleteNews(id), onSuccess: invalidate });

  return (
    <>
      <PageHeader title="News" description="Announcements shown on the member dashboard" tabs={SYSTEM_TABS} />

      <Card>
        <h3 className="card-title">Post an announcement</h3>
        <form onSubmit={form.handleSubmit((values) => create.mutate(values))} noValidate>
          <FormGroup label="Title" error={form.formState.errors.title?.message}>
            <Input {...form.register('title')} />
          </FormGroup>
          <FormGroup label="Body" error={form.formState.errors.body?.message}>
            <Textarea rows={4} {...form.register('body')} />
          </FormGroup>
          <FormGroup label="Image (optional)" hint="Shown as a popup banner on the member dashboard">
            <input
              ref={imageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
            />
          </FormGroup>
          <Button type="submit" variant="gold" disabled={create.isPending}>
            {create.isPending ? 'Publishing…' : 'Publish'}
          </Button>
        </form>
      </Card>

      <Card>
        <h3 className="card-title">Published</h3>
        {isLoading ? (
          <Spinner />
        ) : (
          <ul className="news-list">
            {data?.map((item) => (
              <li key={item.id}>
                <div>
                  <strong>{item.title}</strong>
                  <p className="text-muted small">{formatDateTime(item.created_at)}</p>
                  <p>{item.body}</p>
                  {item.image_url ? (
                    <img
                      src={item.image_url}
                      alt=""
                      style={{ maxWidth: 160, maxHeight: 120, borderRadius: 6, marginTop: 8, display: 'block' }}
                    />
                  ) : null}
                </div>
                <Chip as="button" tone="danger" onClick={() => remove.mutate(item.id)}>
                  Delete
                </Chip>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
